import {Component, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {ActivatedRoute, ParamMap, Router} from "@angular/router"
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {AuthService} from "@common/services/auth/auth.service"
import {SiloService} from "@common/services/auth/silo.service"
import {RegisterUserSsoGQL} from "@common/components/handle-sso-signup-callback/handle-sso-signup-callback.generated"
import {FullPageFeedbackComponent} from "@common/components/full-page-feedback/full-page-feedback.component"
import {LoginProvider} from "@generated"
import {z} from "zod"
import {extractErrorInfo} from "@common/helpers/api/errors"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-handle-sso-signup-callback",
    imports: [FullPageFeedbackComponent, MatProgressSpinnerModule],
    templateUrl: "./handle-sso-signup-callback.component.html",
    styleUrl: "./handle-sso-signup-callback.component.scss",
})
export class HandleSsoSignupCallbackComponent implements OnInit {
    readonly auth = inject(AuthService)
    readonly silo = inject(SiloService)
    readonly activatedRoute = inject(ActivatedRoute)
    readonly notifications = inject(NotificationsService)
    readonly router = inject(Router)
    readonly registerUserSsoGql = inject(RegisterUserSsoGQL)

    readonly $provider = signal<"google" | "microsoft" | undefined>(undefined)

    readonly $isVerifying = signal(true)
    readonly $error = signal<string | null>(null)

    ngOnInit() {
        const parsedData = z.object({provider: z.literal("google").or(z.literal("microsoft"))}).parse(this.activatedRoute.snapshot.data)
        this.$provider.set(parsedData.provider)
        switch (this.$provider()) {
            case "google":
                void this.handleOAuthCallback(LoginProvider.Google, this.activatedRoute.snapshot.queryParamMap)
                break
            case "microsoft": {
                const paramMap = new URLSearchParams(this.activatedRoute.snapshot.fragment ?? "")
                void this.handleOAuthCallback(LoginProvider.Microsoft, paramMap)
            }
        }
    }

    private handleOAuthCallback = async (provider: LoginProvider, paramMap: ParamMap | URLSearchParams) => {
        const error = paramMap.get("error_description") ?? paramMap.get("error")
        if (error) {
            this.$error.set(error)
            this.$isVerifying.set(false)
            return
        }

        const state = paramMap.get("state")
        if (!state) {
            this.$error.set("Missing state parameter")
            this.$isVerifying.set(false)
            return
        }

        const stateData = z.object({csrfToken: z.string(), flow: z.string().optional()}).parse(JSON.parse(atob(state)))
        if (stateData.csrfToken !== this.auth.csrfToken) {
            this.$error.set("Invalid CSRF token")
            this.$isVerifying.set(false)
            return
        }

        const code = paramMap.get("code") ?? paramMap.get("id_token")
        if (!code) {
            this.$error.set("No authorization code found")
            this.$isVerifying.set(false)
            return
        }

        try {
            // Register the user (or auto-login if already registered)
            // Redirect URI is constructed server-side from FRONTEND_ENDPOINT
            const result = await mutateThrowingErrors(this.registerUserSsoGql)({
                provider,
                code,
            })

            if (result?.registerUserSso) {
                const response = result.registerUserSso

                // Store the token and complete login
                this.auth.storeToken(response.token)
                const success = await this.auth.completeLogin({user: {id: response.user.uuid}, token: response.token})

                if (success) {
                    // Activate the silo with the user's membership for proper authorization
                    if (response.membership && response.organization) {
                        this.silo.activateMembership({
                            id: response.membership.uuid,
                            role: response.membership.role,
                            organization: {
                                id: response.organization.uuid,
                                name: response.organization.name,
                                type: response.organization.type,
                                stripeSubscriptionStatus: response.organization.stripeSubscriptionStatus,
                                trialEndsAt: response.organization.trialEndsAt,
                            },
                        })
                    }

                    // Redirect based on whether user existed and onboarding status
                    if (response.isExistingUser && response.onboardingCompleted) {
                        // Existing user with completed onboarding - go to home
                        await this.router.navigate(["/"])
                    } else {
                        // New user or existing user without completed onboarding - go to onboarding
                        await this.router.navigate(["/onboarding"])
                    }
                } else {
                    this.$error.set("Failed to complete login. Please try again.")
                    this.$isVerifying.set(false)
                }
            }
        } catch (error) {
            const errorInfo = extractErrorInfo(error)
            if (errorInfo.code === 409) {
                this.$error.set("This email address is already registered. Please sign in instead.")
            } else if (errorInfo.code === 429) {
                this.$error.set(errorInfo.message)
            } else {
                this.$error.set(errorInfo.message || "Registration failed. Please try again.")
            }
            this.$isVerifying.set(false)
        }
    }

    navigateToLogin(): void {
        this.router.navigate(["/login"])
    }

    navigateToSignUp(): void {
        this.router.navigate(["/95ngsj3e/sign-up"])
    }
}
